import { queryOptions, useQuery } from '@tanstack/react-query';

import { PodMutation } from '@cast/types-patching-engine';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const POD_MUTATIONS_QUERY_KEY = 'autoscaler-pod-mutations';

export const getPodMutationsQueryKey = (
  clusterId?: string,
  pageLimit?: number
) => {
  if (clusterId === undefined) return [POD_MUTATIONS_QUERY_KEY] as const;
  if (pageLimit === undefined)
    return [POD_MUTATIONS_QUERY_KEY, clusterId] as const;
  return [POD_MUTATIONS_QUERY_KEY, clusterId, pageLimit] as const;
};

const getPodMutationsQueryOptions = ({
  organizationId,
  clusterId,
  enabled = true,
  pageLimit = 999,
}: {
  organizationId?: string;
  clusterId?: string;
  enabled?: boolean;
  pageLimit?: number;
}) =>
  queryOptions({
    queryKey: getPodMutationsQueryKey(clusterId, pageLimit),
    queryFn: async () => {
      const {
        data: { items },
      } = await apiClient.autoscaler.getPodMutations({
        organizationId: organizationId!,
        clusterId: clusterId!,
        pageLimit: '' + pageLimit, // TODO: probably should just be a number
      });
      return items;
    },
    enabled: enabled && !!clusterId && !!organizationId,
  });

type UsePodMutationsQuery = {
  isLoading: boolean;
  podMutations?: PodMutation[];
  error: unknown;
  refetch?: () => void;
};

export const usePodMutationsQuery = (
  clusterId?: string,
  enabled = true,
  pageLimit = 999
): UsePodMutationsQuery => {
  const { currentOrganization } = useOrganizations();

  const {
    isLoading,
    data: podMutations,
    error,
    refetch,
  } = useQuery(
    getPodMutationsQueryOptions({
      organizationId: currentOrganization?.id,
      clusterId,
      enabled,
      pageLimit,
    })
  );

  return {
    isLoading,
    podMutations: podMutations ?? [], // TODO: add pagination UI-4204
    error,
    refetch,
  };
};
